/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import { Switch } from '@coursera-web/cds-labs';

import { InlineNotification, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import {
  disableTwoFactorAuthentication,
  enableTwoFactorAuthentication,
  loadTwoFactorAuthenticationSettings,
} from 'bundles/account-settings/actions/SettingsActionCreators';
import TwoFactorAuthenticationModal from 'bundles/account-settings/components/sections/two-factor/TwoFactorAuthenticationModal';
import { TwoFactorAuthenticationStatus } from 'bundles/account-settings/constants/SettingsConstants';
import { isDisabled, isEnabled } from 'bundles/account-settings/utils/TwoFactorAuthenticationUtils';

import _t from 'i18n!nls/account-settings';

type Props = {
  available: boolean;
  state?: string;
  qrCode?: string | null;
  invalidFields?: string[];
  updating?: boolean;
  success?: boolean;
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  headingRow: css`
    display: flex;
    align-items: flex-start;
    gap: var(--cds-spacing-200);
  `,
  headingLeft: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    flex: 1;
    min-width: 0;
    flex-wrap: wrap;
  `,
  heading: css`
    && {
      margin: 0;
    }
  `,
  toggle: css`
    flex-shrink: 0;
  `,
  description: css`
    && {
      margin: 0;
      color: var(--cds-color-neutral-primary-weak);
    }
  `,
};

const Settings = ({
  available,
  state = '',
  qrCode = null,
  invalidFields = [],
  updating = false,
  success = false,
}: Props) => {
  const track = useTracker();
  const switchRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (available) {
      loadTwoFactorAuthenticationSettings();
    }
  }, [available]);

  if (!available) {
    return null;
  }

  const enabled = isEnabled(state);
  const disabled = isDisabled(state);
  const isLoading = !enabled && !disabled;

  const isEnabling =
    state === TwoFactorAuthenticationStatus.ENABLING_CONFIRMING_PASSWORD ||
    state === TwoFactorAuthenticationStatus.ENABLING_CONFIRMING_CODE;
  const isDisabling = state === TwoFactorAuthenticationStatus.DISABLING_CONFIRMING_PASSWORD;
  const switchChecked = (enabled && !isDisabling) || isEnabling;
  const handleToggle = () => {
    track('click_checkbox', {
      checkbox: { name: 'account_setting_two_factor_auth_toggle', checked: !enabled },
    });
    if (enabled) {
      disableTwoFactorAuthentication();
    } else {
      enableTwoFactorAuthentication();
    }
  };

  return (
    <div css={styles.container}>
      <div css={styles.headingRow}>
        <div css={styles.headingLeft}>
          <Typography2 variant="subtitleLarge" component="h2" css={styles.heading}>
            {_t('Two factor authentication')}
          </Typography2>
        </div>
        {!isLoading && (
          <div css={styles.toggle}>
            <Switch
              ref={switchRef}
              selected={switchChecked}
              disabled={updating}
              onToggle={handleToggle}
              aria-label={_t('Two factor authentication')}
              hideTooltip
            />
          </div>
        )}
      </div>

      {isLoading ? (
        <Typography2 variant="bodySecondary" component="p" color="supportText" css={styles.description}>
          {_t('Loading')}
        </Typography2>
      ) : (
        <Typography2 variant="bodySecondary" component="p" color="supportText" css={styles.description}>
          {_t(
            `Two-factor authentication adds an additional layer of security to your Coursera account. Each time you log in to Coursera, you will be asked to enter a unique code that is only available on your mobile phone. This extra protection ensures that you are the only one who will have access to your Coursera account and courses.`
          )}
        </Typography2>
      )}

      <div role="status" aria-live="polite" aria-atomic="true">
        {success && (
          <InlineNotification severity="success">
            {enabled
              ? _t('Two-factor authentication has been enabled.')
              : _t('Two-factor authentication has been disabled.')}
          </InlineNotification>
        )}
      </div>

      <TwoFactorAuthenticationModal
        state={state}
        qrCode={qrCode}
        invalidFields={invalidFields}
        updating={updating}
        returnFocusRef={switchRef}
      />
    </div>
  );
};

export default Settings;
